import { createSlice, createEntityAdapter, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/rootState';
import type { UIElementInstance } from '@/modules/uiElements/types';

const uiElementsAdapter = createEntityAdapter<UIElementInstance, string>({
  selectId: (el) => el.instanceId,
  sortComparer: (a, b) => a.beginTime - b.beginTime,
});

export interface UIElementsState extends ReturnType<typeof uiElementsAdapter.getInitialState> {
  idsBySceneId: Record<string, string[]>;
}

const initialState: UIElementsState = {
  ...uiElementsAdapter.getInitialState(),
  idsBySceneId: {},
};

const slice = createSlice({
  name: 'uiElements',
  initialState,
  reducers: {
    setSceneUIElements: (
      state,
      action: PayloadAction<{ sceneInstanceId: string; elements: UIElementInstance[] }>
    ) => {
      const { sceneInstanceId, elements } = action.payload;
      uiElementsAdapter.upsertMany(state, elements);
      state.idsBySceneId[sceneInstanceId] = elements.map((el) => el.instanceId);
    },

    addUIElement: (state, action: PayloadAction<UIElementInstance>) => {
      uiElementsAdapter.upsertOne(state, action.payload);
      const sceneId = action.payload.sceneInstanceId;
      const elementId = action.payload.instanceId;
      const sceneList = state.idsBySceneId[sceneId] || [];
      if (!sceneList.includes(elementId)) {
        state.idsBySceneId[sceneId] = [...sceneList, elementId];
      }
    },

    updateUIElement: (
      state,
      action: PayloadAction<{ instanceId: string; updates: Partial<UIElementInstance> }>
    ) => {
      const { instanceId, updates } = action.payload;
      uiElementsAdapter.updateOne(state, { id: instanceId, changes: updates });
    },

    removeUIElement: (
      state,
      action: PayloadAction<{ instanceId: string; sceneInstanceId: string }>
    ) => {
      const { instanceId, sceneInstanceId } = action.payload;
      uiElementsAdapter.removeOne(state, instanceId);
      const sceneList = state.idsBySceneId[sceneInstanceId] || [];
      state.idsBySceneId[sceneInstanceId] = sceneList.filter((id) => id !== instanceId);
    },

    clearUIElementsForScene: (state, action: PayloadAction<string>) => {
      const sceneId = action.payload;
      const ids = state.idsBySceneId[sceneId] || [];
      uiElementsAdapter.removeMany(state, ids);
      delete state.idsBySceneId[sceneId];
    },
  },
});

export const {
  setSceneUIElements,
  addUIElement,
  updateUIElement,
  removeUIElement,
  clearUIElementsForScene,
} = slice.actions;

export default slice.reducer;

// Selectors
const selectors = uiElementsAdapter.getSelectors<RootState>((state) => state.uiElements);

export const selectUIElementById = (state: RootState, id: string) =>
  selectors.selectById(state, id);

const NO_UI_ELEMENTS: UIElementInstance[] = [];

/** A scene's UI elements by start time, memoised so it returns the same array until they change; none without a scene. */
export const selectUIElementsBySceneId = createSelector(
  [
    (state: RootState, sceneInstanceId: string | undefined) => (sceneInstanceId ? state.uiElements.idsBySceneId[sceneInstanceId] : undefined),
    (state: RootState) => state.uiElements.entities,
  ],
  (ids, entities): UIElementInstance[] =>
    ids?.length
      ? ids
          .map((id) => entities[id])
          .filter((el): el is UIElementInstance => Boolean(el))
          .sort((a, b) => a.beginTime - b.beginTime)
      : NO_UI_ELEMENTS
);

export const selectAllUIElements = (state: RootState) => selectors.selectAll(state);
